import { ReferenceCodeDisplay } from '@chiroup/core/types/ReferenceCode.type';

/**
 * The ten diagnoses the spec fixes for the sandbox Assessment step (Dr. B,
 * CJ > Dev Story board, 2026-09-01), in the spec's order.
 *
 * The authenticated sandbox narrows the clinic's real condition list to these
 * ten by name. The anonymous walkthrough supplies the same required protocol
 * fields from its published stock snapshot.
 */

/** The shape of a row in the conditionList response that the sandbox uses.
 *  (The endpoint returns more; these are the fields the diagnosis step and
 *  the objective seeding consume — same fields NotesConditionsPickerModal
 *  reads.) */
export interface ConditionListRow {
  ID: number;
  name?: string;
  commonName?: string;
  title?: string;
  /** The list endpoint aliases commonImg as image. img is a separate clinical
   * illustration retained as the existing fallback. */
  image?: string | null;
  img?: string | null;
  regions?: string[];
  muscles?: number[];
  typicallyPositiveEvals?: number[];
  typicallyNegativeEvals?: number[];
  referenceCodes?: ReferenceCodeDisplay[];
  favorite?: number | boolean;
  userVersion?: number;
  clinicVersion?: number;
}

interface SandboxDiagnosisSpec {
  /** Display order from the spec. */
  order: number;
  /** Label shown if we ever need a fallback; primarily documentation. */
  specName: string;
  /** Normalized name prefixes that identify the stock condition. A row
   *  matches when its normalized name starts with any of these — tolerant of
   *  punctuation/suffix drift ("Lumbar Spondylosis/DJD/DDD",
   *  "Lumbar Radiculopathy - Sciatic"). */
  match: string[];
}

/** Lowercase and strip everything but letters/digits so "/", "-", spacing and
 *  case differences never break matching. */
export const normalizeConditionName = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '');

export const SANDBOX_DIAGNOSES: SandboxDiagnosisSpec[] = [
  { order: 1, specName: 'Lumbar Disc Lesion', match: ['lumbardisclesion'] },
  { order: 2, specName: 'Lumbar Facet Syndrome', match: ['lumbarfacet'] },
  { order: 3, specName: 'Lumbar Radiculopathy', match: ['lumbarradiculopathy'] },
  {
    order: 4,
    specName: 'Lumbar Segmental Joint Dysfunction',
    // Stock libraries name this one "Restriction" (cf. "Cervical Segmental
    // Joint Restriction"), while the spec says "Dysfunction".
    match: [
      'lumbarsegmentaljointdysfunction',
      'lumbarsegmentaldysfunction',
      'lumbarsegmentaljointrestriction',
      'lumbarsegmentalrestriction',
    ],
  },
  { order: 5, specName: 'Lumbar Spondylosis / DDD', match: ['lumbarspondylosis'] },
  { order: 6, specName: 'Lumbar Sprain/Strain', match: ['lumbarsprainstrain', 'lumbarsprain'] },
  { order: 7, specName: 'Sacroiliac Joint Dysfunction', match: ['sacroiliacjointdysfunction'] },
  {
    order: 8,
    specName: 'Lumbar Instability',
    match: ['lumbarinstability', 'lumbarspineinstability', 'spinalinstability'],
  },
  {
    order: 9,
    specName: 'Extension-Biased LBP',
    match: ['extensionbiasedlbp', 'extensionbiasedlowbackpain'],
  },
  { order: 10, specName: 'Lower Crossed Syndrome', match: ['lowercrossedsyndrome'] },
];

const rowDisplayName = (row: ConditionListRow): string =>
  row.title || row.name || row.commonName || '';

/**
 * Narrow the clinic's condition list to the sandbox's ten diagnoses, in spec
 * order. Matching runs four tiers — exact name, name prefix, name contains,
 * then all-words-present — so stock variants like "Lumbar Radiculopathy -
 * Sciatic", "Clinical Lumbar Instability", or reordered names like
 * "Segmental Joint Dysfunction - Lumbar" still resolve. Each row can satisfy
 * only one diagnosis; one with no match is dropped (better a shorter list
 * than a dead card).
 */
const resolveSandboxConditions = (
  rows: ConditionListRow[]
): { spec: SandboxDiagnosisSpec; row?: ConditionListRow }[] => {
  const normalized = rows.map(row => ({ row, key: normalizeConditionName(rowDisplayName(row)) }));
  const claimed = new Set<number>();

  return SANDBOX_DIAGNOSES.map(spec => {
    const available = normalized.filter(({ row }) => !claimed.has(row.ID));
    const specTokens = spec.specName
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);

    const row = (
      available.find(({ key }) => spec.match.includes(key)) ||
      available.find(({ key }) => spec.match.some(prefix => key.startsWith(prefix))) ||
      available.find(({ key }) => spec.match.some(pattern => key.includes(pattern))) ||
      available.find(({ key }) => specTokens.every(token => key.includes(token)))
    )?.row;

    if (row) claimed.add(row.ID);

    return { spec, row };
  });
};

export const matchSandboxConditions = (rows: ConditionListRow[]): ConditionListRow[] =>
  resolveSandboxConditions(rows)
    .map(({ row }) => row)
    .filter((row): row is ConditionListRow => !!row);

/**
 * True when the cached condition list is the `basicList` variant, which has no
 * protocol fields. `useConditions` caches every variant under the one
 * ['conditions'] key with a 1-hour staleTime, and Clinical Skills → Conditions
 * fetches basicList — a page trial prospects naturally explore. Mounting the
 * diagnosis step onto that cache would seed NO myofascial/orthopedic
 * suggestions (tester note #17: empty Myofascial module, no Accept
 * Suggestions button). Discriminator: the full list maps `muscles` to an
 * array on every row (possibly empty); the basic query never selects it.
 */
export const isBasicConditionList = (rows: ConditionListRow[]): boolean =>
  rows.length > 0 && rows.every(row => row.muscles === undefined);

/** Spec diagnoses that found no row in the given library list — used by the
 *  diagnosis step's dev diagnostic so a missing card is explained instead of
 *  silently dropped. */
export const unmatchedSandboxDiagnoses = (rows: ConditionListRow[]): string[] =>
  resolveSandboxConditions(rows)
    .filter(({ row }) => !row)
    .map(({ spec }) => spec.specName);
